import React, { useState, useEffect } from 'react';
import { 
  Sliders, 
  RefreshCw, 
  Settings, 
  Shield, 
  Check, 
  AlertCircle, 
  X, 
  Link2, 
  Key, 
  Lock, 
  Globe, 
  Database, 
  Save,
  Info,
  Eye,
  EyeOff,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';

interface RiskNumberSettings {
  apiKey: string;
  apiSecret: string;
  webhookUrl: string;
  autoSync: boolean;
  syncFrequency: 'realtime' | 'hourly' | 'daily';
  notifyOnNewProspects: boolean;
  autoAssignProspects: boolean;
  defaultAssignee: string;
  privacySettings: {
    encryptData: boolean;
    limitDataAccess: boolean;
    retentionPeriodDays: number;
  };
}

const RiskNumberIntegration: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<RiskNumberSettings>({
    apiKey: '',
    apiSecret: '',
    webhookUrl: `${window.location.origin}/api/risknumber/webhook`,
    autoSync: true,
    syncFrequency: 'daily',
    notifyOnNewProspects: true,
    autoAssignProspects: false,
    defaultAssignee: '',
    privacySettings: {
      encryptData: true,
      limitDataAccess: true,
      retentionPeriodDays: 365
    }
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);
  const [totalProspects, setTotalProspects] = useState(0);

  // Load saved credentials on component mount
  useEffect(() => {
    if (user) {
      loadRiskNumberCredentials();
    }
  }, [user]);

  const loadRiskNumberCredentials = async () => {
    if (!user) return;
    
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('user_credentials')
        .select('provider, credentials')
        .eq('user_id', user.id)
        .eq('provider', 'risknumber_api')
        .single();
      
      if (error) {
        if (error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
          console.error('Error loading Risk Number credentials:', error);
          toast.error('Failed to load Risk Number API settings');
        }
        setConnectionStatus('disconnected');
      } else if (data) {
        // Update settings with saved credentials
        setSettings({
          ...settings,
          apiKey: data.credentials.apiKey || '',
          apiSecret: data.credentials.apiSecret || '',
          webhookUrl: data.credentials.webhookUrl || settings.webhookUrl,
          autoSync: data.credentials.autoSync !== false,
          syncFrequency: data.credentials.syncFrequency || 'daily',
          notifyOnNewProspects: data.credentials.notifyOnNewProspects !== false,
          autoAssignProspects: data.credentials.autoAssignProspects || false,
          defaultAssignee: data.credentials.defaultAssignee || '',
          privacySettings: {
            encryptData: data.credentials.privacySettings?.encryptData !== false,
            limitDataAccess: data.credentials.privacySettings?.limitDataAccess !== false,
            retentionPeriodDays: data.credentials.privacySettings?.retentionPeriodDays || 365
          }
        });
        
        setConnectionStatus('connected');
        setLastSyncTime(data.credentials.lastSync ? new Date(data.credentials.lastSync) : null);
        setTotalProspects(data.credentials.totalProspects || 0);
      }
    } catch (error) {
      console.error('Error loading Risk Number credentials:', error);
      toast.error('Failed to load Risk Number API settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!user) return;
    
    setIsSaving(true);
    setErrorMessage(null);
    
    try {
      // Validate required fields
      if (!settings.apiKey.trim()) {
        throw new Error('API Key is required');
      }
      
      if (!settings.apiSecret.trim()) {
        throw new Error('API Secret is required');
      }
      
      // Save credentials to database
      const { error } = await supabase
        .from('user_credentials')
        .upsert({
          user_id: user.id,
          provider: 'risknumber_api',
          credentials: {
            apiKey: settings.apiKey,
            apiSecret: settings.apiSecret,
            webhookUrl: settings.webhookUrl,
            autoSync: settings.autoSync,
            syncFrequency: settings.syncFrequency,
            notifyOnNewProspects: settings.notifyOnNewProspects,
            autoAssignProspects: settings.autoAssignProspects,
            defaultAssignee: settings.defaultAssignee,
            privacySettings: settings.privacySettings,
            lastSync: lastSyncTime?.toISOString() || new Date().toISOString(),
            totalProspects
          }
        });
      
      if (error) throw error;
      
      setConnectionStatus('connected');
      toast.success('Risk Number API settings saved successfully');
    } catch (error) {
      console.error('Error saving Risk Number credentials:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to save settings');
      toast.error('Failed to save Risk Number API settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setErrorMessage(null);
    
    try {
      // In a real implementation, this would make an API call to test the connection
      // For this demo, we'll simulate a successful connection
      
      // Validate required fields
      if (!settings.apiKey.trim() || !settings.apiSecret.trim()) {
        throw new Error('API Key and API Secret are required');
      }
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Randomly succeed or fail for demo purposes
      const success = Math.random() > 0.3;
      
      if (!success) {
        throw new Error('Failed to connect to Risk Number API. Please check your credentials and try again.');
      }
      
      setConnectionStatus('connected');
      setLastSyncTime(new Date());
      setTotalProspects(Math.floor(Math.random() * 50) + 10);
      
      toast.success('Successfully connected to Risk Number API');
    } catch (error) {
      console.error('Error testing Risk Number connection:', error);
      setConnectionStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to connect to Risk Number API');
      toast.error('Failed to connect to Risk Number API');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsTesting(true);
      
      // In a real implementation, this would make an API call to sync data
      // For this demo, we'll simulate a successful sync
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setLastSyncTime(new Date());
      const newProspects = Math.floor(Math.random() * 5);
      setTotalProspects(prev => prev + newProspects);
      
      toast.success(`Synced successfully. ${newProspects} new prospects found.`);
    } catch (error) {
      console.error('Error syncing with Risk Number API:', error);
      toast.error('Failed to sync with Risk Number API');
    } finally {
      setIsTesting(false);
    }
  };

  const getStatusBadge = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <Check className="h-3 w-3 mr-1" />
            Connected
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <X className="h-3 w-3 mr-1" />
            Disconnected
          </span>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-8 w-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading Risk Number API settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Risk Number API Integration</h2>
          <p className="mt-1 text-sm text-gray-500">
            Connect to whatismyrisknumber.com to automatically import prospect data
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={isTesting || isSaving}
            className="py-1.5 px-3"
          >
            {isTesting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Link2 className="h-4 w-4 mr-2" />
                Test Connection
              </>
            )}
          </Button>
          
          <Button
            onClick={handleSaveSettings}
            disabled={isTesting || isSaving}
            className="py-1.5 px-3"
          >
            {isSaving ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Settings
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Connection Status */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-orange-100 rounded-full">
              <Sliders className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-medium text-gray-900">Risk Number API</h3>
                {getStatusBadge()}
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {connectionStatus === 'connected' 
                  ? `Last synced: ${lastSyncTime ? lastSyncTime.toLocaleString() : 'Never'}`
                  : 'Not connected'}
              </p>
            </div>
          </div>
          
          {connectionStatus === 'connected' && (
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={isTesting}
              className="py-1.5 px-3"
            >
              {isTesting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Sync Now
                </>
              )}
            </Button>
          )}
        </div>
        
        {connectionStatus === 'connected' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-500">Total Prospects</div>
              <div className="text-2xl font-bold text-gray-900">{totalProspects}</div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-500">Last Sync</div>
              <div className="text-2xl font-bold text-gray-900">
                {lastSyncTime ? lastSyncTime.toLocaleDateString() : 'Never'}
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm text-gray-500">Sync Status</div>
              <div className="text-2xl font-bold text-green-600">Active</div>
            </div>
          </div>
        )}
        
        {errorMessage && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-red-800">Connection Error</h4>
                <p className="mt-1 text-sm text-red-700">{errorMessage}</p>
              </div>
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">API Credentials</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Key
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Key className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showApiKey ? "text" : "password"}
                    value={settings.apiKey}
                    onChange={(e) => setSettings({ ...settings, apiKey: e.target.value })}
                    className="pl-10 pr-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    placeholder="Enter your API key"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showApiKey ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  API Secret
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showApiSecret ? "text" : "password"}
                    value={settings.apiSecret}
                    onChange={(e) => setSettings({ ...settings, apiSecret: e.target.value })}
                    className="pl-10 pr-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                    placeholder="Enter your API secret"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiSecret(!showApiSecret)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showApiSecret ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              You can find your API credentials in your whatismyrisknumber.com account settings.
            </p>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Webhook Configuration</h4>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Webhook URL
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Globe className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={settings.webhookUrl}
                  onChange={(e) => setSettings({ ...settings, webhookUrl: e.target.value })}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
                  placeholder="https://your-domain.com/api/risknumber/webhook"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Configure this URL in your whatismyrisknumber.com account to receive real-time prospect notifications.
              </p>
            </div>
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">Sync Preferences</h4>
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.autoSync}
                    onChange={(e) => setSettings({ ...settings, autoSync: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Enable automatic sync</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 ml-6">
                  When enabled, prospect data will be automatically synced based on your frequency settings
                </p>
              </div>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Sync Frequency</label>
                <select
                  value={settings.syncFrequency}
                  onChange={(e) => setSettings({ ...settings, syncFrequency: e.target.value as 'realtime' | 'hourly' | 'daily' })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  disabled={!settings.autoSync}
                >
                  <option value="realtime">Real-time (via webhook)</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                </select>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.notifyOnNewProspects}
                    onChange={(e) => setSettings({ ...settings, notifyOnNewProspects: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Notify me of new prospects</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.autoAssignProspects}
                    onChange={(e) => setSettings({ ...settings, autoAssignProspects: e.target.checked })}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Auto-assign new prospects</span>
                </label>
              </div>
              
              {settings.autoAssignProspects && (
                <div className="ml-6">
                  <label className="block text-sm text-gray-700 mb-1">Default Assignee</label>
                  <select
                    value={settings.defaultAssignee}
                    onChange={(e) => setSettings({ ...settings, defaultAssignee: e.target.value })}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  >
                    <option value="">Select an assignee</option>
                    <option value="Sarah Johnson">Sarah Johnson</option>
                    <option value="Michael Brown">Michael Brown</option>
                    <option value="David Wilson">David Wilson</option>
                  </select>
                </div>
              )}
            </div>
          </div>
          
          {/* Privacy Settings */}
          <div className="mt-6 bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="h-4 w-4 text-primary" />
              Privacy Settings
            </h4>
            <div className="space-y-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.privacySettings.encryptData}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacySettings: {
                      ...settings.privacySettings,
                      encryptData: e.target.checked
                    }
                  })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-600">Encrypt prospect data</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={settings.privacySettings.limitDataAccess}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacySettings: {
                      ...settings.privacySettings,
                      limitDataAccess: e.target.checked
                    }
                  })}
                  className="rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span className="text-sm text-gray-600">Limit data access to authorized users only</span>
              </label>
              
              <div>
                <label className="block text-sm text-gray-700 mb-1">Data Retention Period (days)</label>
                <input
                  type="number"
                  min="30"
                  max="3650"
                  value={settings.privacySettings.retentionPeriodDays}
                  onChange={(e) => setSettings({
                    ...settings,
                    privacySettings: {
                      ...settings.privacySettings,
                      retentionPeriodDays: parseInt(e.target.value)
                    }
                  })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* API Documentation */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">API Documentation</h3>
        <div className="prose prose-sm max-w-none">
          <p>
            The Risk Number API allows you to programmatically access prospect data from whatismyrisknumber.com.
            Below are the key endpoints and parameters you'll need to use.
          </p>
          
          <h4 className="font-medium mt-4">Base URL</h4>
          <pre className="bg-gray-50 p-3 rounded-md overflow-x-auto">
            https://api.whatismyrisknumber.com/v1
          </pre>
          
          <h4 className="font-medium mt-4">Authentication</h4>
          <p>
            All API requests require authentication using your API key and secret. Include these in the request headers:
          </p>
          <pre className="bg-gray-50 p-3 rounded-md overflow-x-auto">
            X-API-Key: your_api_key
            X-API-Secret: your_api_secret
          </pre>
          
          <h4 className="font-medium mt-4">Endpoints</h4>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <code className="bg-gray-100 px-1 rounded">GET /prospects</code> - List all prospects
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">GET /prospects/{'{id}'}</code> - Get a specific prospect
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">GET /prospects/sync</code> - Manually trigger a sync
            </li>
          </ul>
          
          <h4 className="font-medium mt-4">Webhook Events</h4>
          <p>
            Configure webhooks to receive real-time notifications when new prospects complete the risk assessment.
            Webhook payloads include:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <code className="bg-gray-100 px-1 rounded">prospect.created</code> - When a new prospect is created
            </li>
            <li>
              <code className="bg-gray-100 px-1 rounded">prospect.updated</code> - When a prospect's data is updated
            </li>
          </ul>
          
          <div className="bg-blue-50 p-4 rounded-lg mt-4">
            <div className="flex">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-blue-800">Need Help?</h4>
                <p className="mt-1 text-sm text-blue-700">
                  For detailed API documentation and implementation examples, visit the 
                  <a href="https://docs.whatismyrisknumber.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                    Risk Number Developer Portal
                  </a>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskNumberIntegration;